import { QueryObserverResult, RefetchOptions, useQuery } from '@tanstack/react-query';

import { QUERY_STALE_TIMES } from '../../shared/providers/react-query-provider';
import { DossierId } from '../dossier/types';

import { useAppSelector } from '../../shared/redux';

import { APICallStatus } from '../../design-system/types';

import { useGetPhoneCalls } from '../phoning/queries';

import { DiscussionItem, DisplayDiscussion, Message, NormalizedDiscussion } from './types';
import { getDiscussion } from './api';
import {
    addAttachmentLinkToMessage,
    getIsResponseRequiredState,
    groupDiscussionItemByDate,
} from './operations';
import { getDiscussion as getDiscussionSelector } from './selectors';

export const getDiscussionMessagesQueryKey = (dossierId: DossierId) => [
    'discussion',
    dossierId,
    'all',
];

export type AllDiscussionMessagesResponse = {
    dossierId: DossierId;
    messages: Message[];
    isResponseRequired: boolean;
};

export const useAllDiscussionMessages = (dossierId: DossierId) => {
    return useQuery<AllDiscussionMessagesResponse>({
        queryKey: getDiscussionMessagesQueryKey(dossierId),
        queryFn: async () => {
            const allMessages: Record<string, Message> = {};
            let isResponseRequired = false;
            let currentPage = 1;
            let hasNextPage = true;

            while (hasNextPage) {
                const response: NormalizedDiscussion = await getDiscussion(dossierId, currentPage);
                const discussionData = response[dossierId];

                if (discussionData.messages) {
                    discussionData.messages?.forEach((message) => {
                        allMessages[message.id] = message;
                    });
                }

                hasNextPage = discussionData.pagination.links.next !== undefined;
                currentPage = discussionData.pagination.currentPage + 1;
                // eslint-disable-next-line prefer-destructuring
                isResponseRequired = discussionData.isResponseRequired;
            }

            return {
                dossierId,
                messages: Object.values(allMessages),
                isResponseRequired,
            };
        },
        enabled: !!dossierId,
        staleTime: QUERY_STALE_TIMES.DISCUSSION,
    });
};

export const useLastTravellerMessage = (dossierId: DossierId): Message | null => {
    const { data } = useAllDiscussionMessages(dossierId);
    const { messages } = data || {};

    if (!messages || messages.length === 0) {
        return null;
    }

    const travellerMessages = messages.filter((message) => message.isFromTraveller);

    if (travellerMessages.length === 0) {
        return null;
    }

    return travellerMessages[travellerMessages.length - 1];
};

export const useIsLastMessageAndFromTraveller = (
    dossierId: DossierId,
    messageId: string | number,
): boolean => {
    const { data } = useAllDiscussionMessages(dossierId);
    const { messages } = data || {};
    if (!messages || messages.length === 0) {
        return false;
    }
    const lastMessage = messages.at(-1);

    return !!lastMessage?.isFromTraveller && lastMessage.id === messageId;
};

export const useIsLastMessageAndFromAgent = (
    dossierId: DossierId,
    messageId: string | number,
): boolean => {
    const { data } = useAllDiscussionMessages(dossierId);
    const { messages } = data || {};
    if (!messages || messages.length === 0) {
        return false;
    }
    const lastMessage = messages.at(-1);

    return !!lastMessage?.isFromAgent && lastMessage.id === messageId;
};

export const useIsFirstAgentMessage = (
    dossierId: DossierId,
    messageId: string | number,
): boolean => {
    const { data } = useAllDiscussionMessages(dossierId);
    const { messages } = data || {};

    if (!messages || messages.length === 0) return false;

    const agentMessages = messages
        .filter((message) => message.isFromAgent)
        .sort((a, b) => a.sendDate.getTime() - b.sendDate.getTime());

    if (agentMessages.length === 0) return false;

    return agentMessages[0].id === messageId;
};

export const useFilteredDiscussionGroupedByDate = (
    dossierId: DossierId,
): DisplayDiscussion & {
    refetch: (
        options?: RefetchOptions | undefined,
    ) => Promise<QueryObserverResult<AllDiscussionMessagesResponse, Error>>;
    status: APICallStatus;
} => {
    const { data, refetch, status } = useAllDiscussionMessages(dossierId);
    const { messages, isResponseRequired } = data || {};

    // @todo to replace after schedule and draft migration
    const discussionLegacy = useAppSelector(getDiscussionSelector(dossierId));
    const { data: phoneCalls = [] } = useGetPhoneCalls(dossierId);

    const messageWithAttachment = [...new Set(messages)].map(addAttachmentLinkToMessage);
    const messagesAndPhoneCalls: DiscussionItem[] = [...messageWithAttachment, ...phoneCalls].sort(
        (a, b) => a.sendDate.getTime() - b.sendDate.getTime(),
    );

    const discussionItems = [...new Set(messagesAndPhoneCalls)].reduce(
        groupDiscussionItemByDate,
        {},
    );

    const lastMessage = messageWithAttachment
        .sort((a, b) => a.sendDate.getTime() - b.sendDate.getTime())
        .at(-1);
    const isResponseRequiredState = getIsResponseRequiredState(
        lastMessage?.isFromTraveller,
        Boolean(isResponseRequired),
    );

    return {
        messages: discussionItems,
        scheduledMessage: discussionLegacy.scheduledMessage,
        sendingMessage: discussionLegacy.sendingMessage,
        isResponseRequiredState,
        refetch,
        status,
    };
};

/* START - AB Maturity Latency isLatencyTest ab_maturity_latency */
/**
 * Hook that checks if the agent has sent multiple messages (2 or more).
 * Returns true if agent has sent 2 or more messages (AT1 automatic + real response).
 */
export const useHasAgentSentMultipleMessages = (dossierId: DossierId): boolean => {
    const { data } = useAllDiscussionMessages(dossierId);
    const { messages } = data || {};

    if (!messages || messages.length === 0) return false;

    const agentMessageCount = messages.filter((message) => message.isFromAgent).length;

    return agentMessageCount > 1;
};
/* END - AB Maturity Latency isLatencyTest ab_maturity_latency */

/* START - AT2 EXPERIMENT - Second Message Verification */
/**
 * Hook that returns the exact count of messages sent by the agent in the discussion.
 * Used to determine if the agent is about to send their 2nd message (count === 1).
 */
export const useAgentMessageCount = (dossierId: DossierId): number => {
    const { data } = useAllDiscussionMessages(dossierId);
    const { messages } = data || {};

    if (!messages || messages.length === 0) {
        return 0;
    }

    return messages.filter((message) => message.isFromAgent).length;
};
/* END - AT2 EXPERIMENT - Second Message Verification */
